<template>
  <MainHeader>
    <component
      :title="slideoverTitle"
      :is="slideoverComponent"
      :visible="slideoverVisible"
      :featureFlags="featureFlags"
      :phase3d_care_app="phase3d_care_app"
      v-on:toggle-slideover="closeSlideover"
    />

    <div class="lg:flex lg:items-center lg:justify-between">
      <!-- Left side -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center pr-4 sm:truncate">
          <h1
            class="text-2xl font-bold leading-7 text-cool-gray-900 sm:leading-9 mr-2"
          >
            <span v-if="isOrderLoaded">
              Order Number:
              <a
                v-if="hasWebOrderLink"
                :href="webOrderUrl"
                target="_blank"
                class="link"
              >
                {{ order.SourceOrderNumber }}
              </a>
              <span v-else> {{ order.SourceOrderNumber }} </span>
            </span>
            <span v-else> Orders </span>
          </h1>

          <OrderCartLogos v-if="isOrderLoaded" :cart="cart" />
        </div>

        <ul
          v-if="isOrderLoaded"
          class="mt-1 flex flex-col space-x-4 sm:mt-0 sm:flex-row sm:flex-wrap"
        >
          <li title="Order Number">
            <icon
              set="solid"
              name="hashtag"
              class="w-5 h-5 text-cool-gray-400"
            />
            {{ order.OrderNumber }}
          </li>

          <li title="Order Date">
            <icon
              set="solid"
              name="calendar"
              class="w-5 h-5 text-cool-gray-400"
            />
            <span>{{ orderDate }}</span>
          </li>

          <li title="Customer">
            <icon set="solid" name="user" class="w-5 h-5 text-cool-gray-400" />
            <span>
              <router-link
                class="link"
                :to="{ path: `/customers/details/${order.CustomerID}` }"
                >{{ order.CustomerID }}</router-link
              >
            </span>
          </li>

          <li title="Growing Zone">
            <icon-fgt
              set="solid"
              name="gz"
              title="Growing Zone"
              class="h-5 text-cool-gray-400"
            />
            <span>{{ order.GrowingZone }}</span>
          </li>

          <li title="Print Date">
            <icon
              set="solid"
              name="printer"
              class="w-5 h-5 text-cool-gray-400"
            />
            <span>{{ order.ExpectedShipDate | date }}</span>
          </li>

          <li title="Fulfillment Date">
            <icon set="solid" name="truck" class="w-5 h-5 text-cool-gray-400" />
            <span>{{ order.ExpectedFulfillmentDate | date }}</span>
          </li>

          <li>
            <span
              v-if="Number(order.Cancelled)"
              class="inline-flex items-center px-2 py-0.5 ml-1 rounded text-xs font-medium leading-4 bg-red-200 text-red-800"
            >
              Cancelled
            </span>
            <span
              v-if="orderStatus === 'Restricted'"
              class="inline-flex items-center px-2 py-0.5 ml-1 rounded text-xs font-medium leading-4 bg-red-200 text-red-800"
            >
              Restricted
            </span>
            <span
              v-else-if="order.Designation === 'On Hold'"
              class="inline-flex items-center px-2 py-0.5 ml-1 rounded text-xs font-medium leading-4 bg-yellow-200 text-yellow-800"
            >
              On Hold
            </span>
            <span
              v-if="orderStatus === 'Unprocessed'"
              class="inline-flex items-center px-2 py-0.5 ml-1 rounded text-xs font-medium leading-4 bg-gray-200 text-gray-800"
            >
              Unprocessed
            </span>
          </li>
        </ul>
      </div>

      <!-- Right side  -->
      <div class="mt-5 flex lg:mt-0 lg:ml-4 space-x-2">
        <!-- Payment Status -->
        <span
          v-if="loaded"
          class="inline-flex items-center px-4 py-0.5 rounded-md text-sm font-medium leading-5 border-2 w-"
          :class="this.paymentStatusClasses[this.paymentStatus]"
        >
          {{ this.paymentStatus }}: {{ order.BalanceDue | currency }}
        </span>

        <!-- Buttons -->
        <ActionsMenu v-if="canShowActions" large>
          <ActionsItem
            v-if="userCanEmailCustomer"
            @click="openEmailCustomerSlideover"
          >
            Email Customer
          </ActionsItem>
          <ActionsItem
            v-if="orderCanBeReleased && !restrictedOrder"
            @click="openReleaseSlideover"
            :disabled="!orderIsFreight"
          >
            Release Order
          </ActionsItem>
          <ActionsItem
            v-if="orderCanBeHold && !restrictedOrder"
            @click="openHoldModal"
          >
            Hold Order
          </ActionsItem>
          <ActionsItem
            v-if="!orderIsCancelled"
            :disabled="!orderCanBeCancelled"
            @click="openCancelModal"
          >
            Cancel Order
          </ActionsItem>
          <ActionsItem
            v-if="
              !orderIsCancelled &&
              userCanProcessGuaranteeClaim &&
              !restrictedOrder
            "
            :disabled="!guaranteeCanBeClaimed"
            @click="openGuaranteeClaimSlideover"
          >
            Guarantee Claim
          </ActionsItem>
          <ActionsItem
            v-if="
              !phase3d_care_app &&
              !orderIsCancelled &&
              userCanProcessDormancyClaim &&
              !restrictedOrder
            "
            :disabled="!dormancyCanBeClaimed"
            @click="openDormancyClaimSlideover"
          >
            Dormancy Claim
          </ActionsItem>
          <ActionsItem
            v-if="
              !orderIsCancelled &&
              userCanProcessWarrantyClaim &&
              !restrictedOrder
            "
            :disabled="!warrantyCanBeClaimed"
            @click="openWarrantyClaimSlideover"
          >
            Warranty Claim
          </ActionsItem>
          <ActionsItem
            v-if="
              !orderIsCancelled &&
              userCanProcessExceptionClaim &&
              !restrictedOrder
            "
            :disabled="!exceptionCanBeClaimed"
            @click="openExceptionClaimSlideover"
          >
            Exception Claim
          </ActionsItem>
          <ActionsItem
            v-if="!orderIsCancelled && !restrictedOrder"
            @click="openEditOrderExpectedShipDateSlideover"
          >
            Edit Expected Print Date
          </ActionsItem>
          <ActionsItem
            v-if="userCanUpdateFulfillmentStatus && !restrictedOrder"
            @click="openFulfillmentModal"
          >
            Update Fulfillment Status
          </ActionsItem>
          <ActionsItem
            v-if="!orderIsCancelled"
            @click="openUpdateStateRestrictionModal"
          >
            Check for State Restrictions
          </ActionsItem>
        </ActionsMenu>

        <Button v-if="loaded" v-on:click-handler="openAdvancedSearchSlideover">
          Advanced Search
        </Button>
      </div>
    </div>
  </MainHeader>
</template>

<script>
import auth from "@/api/auth";
import dateFormatter from "@/mixins/dateFormatter";
import moneyFormatter from "@/mixins/moneyFormatter";
import carts from "@/data/carts.json";
import MainHeader from "@/components/elements/MainHeader.vue";
import Button from "@/components/elements/Button.vue";
import OrderSearchSlideover from "./slideovers/OrderSearch.vue";
import OrderReleaseSlideover from "./slideovers//OrderRelease/OrderRelease.vue";
import EmailCustomerSlideover from "./slideovers/EmailCustomer.vue";
import WarrantyClaimSlideover from "./slideovers/WarrantyClaim/WarrantyClaim.vue";
import DormancyClaimSlideover from "./slideovers/DormancyClaim/DormancyClaim.vue";
import GuaranteeClaimSlideover from "./slideovers/GuaranteeClaim/GuaranteeClaim.vue";
import ExceptionClaimSlideover from "./slideovers/ExceptionClaim/ExceptionClaim.vue";
import EditOrderExpectedShipDateSlideover from "./slideovers/EditOrderExpectedShipDate.vue";
import OrderCartLogos from "./OrderCartLogos.vue";

import { ModalBus } from "@/eventBus.js";
import ModalOrderHold from "./modals/OrderHold.vue";
import ModalOrderCancel from "./modals/OrderCancel.vue";
import ModalAddWarranty from "./modals/AddWarranty.vue";
import ModalOrderFulfillmentStatus from "./modals/OrderFulfillmentStatus.vue";
import OrderStateRestrictionUpdate from "./modals/OrderStateRestrictionUpdate";

export default {
  name: "OrderBar",
  components: {
    Button,
    MainHeader,
    OrderCartLogos,
    OrderSearchSlideover,
    OrderReleaseSlideover,
    EmailCustomerSlideover,
    GuaranteeClaimSlideover,
    DormancyClaimSlideover,
    WarrantyClaimSlideover,
    ExceptionClaimSlideover,
    EditOrderExpectedShipDateSlideover,
  },
  mixins: [dateFormatter, moneyFormatter],
  props: {
    order: {
      type: Object,
      required: true,
    },
    loaded: {
      type: Boolean,
      required: true,
      default: false,
    },
    deprecatedOrder: {
      type: Boolean,
      required: true,
      default: false,
    },
    deprecatedOrderLoaded: {
      type: Boolean,
      required: true,
      default: false,
    },
    phase3d_care_app: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showOrderActions: false,
      slideoverComponent: null,
      slideoverTitle: null,
      slideoverVisible: null,
      webOrderCartsBB: [23, 24],
      webOrderCartsFGT: [22, 32],
      webOrderCartsPT: [31],
      paymentStatusClasses: {
        "Balance Due": "border-red-500 text-red-800",
        "Credit Due": "border-green-500 text-green-800",
        "Paid in Full": "border-gray-500 text-gray-500",
      },
      warrantyStart: process.env.VUE_APP_GUARANTEE_WINDOW || 37, //GC end after 97 days, See ET-1007 for details
      warrantyEnd: 372, //Warranty Ends after 372 days, See ET-240 for details
    };
  },
  async created() {},
  computed: {
    featureFlags() {
      return {
        phase3d_care_app: this.phase3d_care_app,
      };
    },
    isOrderLoaded() {
      return this.loaded || this.deprecatedOrderLoaded;
    },
    orderDate() {
      return dateFormatter.format(this.order.OrderDate);
    },
    cart() {
      if (!this.deprecatedOrder) {
        for (const note of this.order.notes) {
          if (note.note && note.note.includes("brand: trees.com")) {
            return carts.find((cart) => cart.ID == 32);
          }
        }
      }
      let cart = carts.find((cart) => cart.ID == this.order.CartID);
      return cart;
    },
    paymentStatus() {
      let balanceDue = parseFloat(this.order?.BalanceDue);
      if (balanceDue > 0) {
        return "Balance Due";
      }
      if (balanceDue < 0) {
        return "Credit Due";
      }
      return "Paid in Full";
    },
    logoName() {
      if ([2, 10, 17, 22].includes(parseInt(this.order.CartID)))
        return "fgt-logo";
      if (
        [11, 14, 27, 26, 16, 25, 30, 23, 24].includes(
          parseInt(this.order.CartID)
        )
      )
        return "bb-logo";
      return false;
    },
    hasWebOrderLink() {
      return this.webOrderCartsBB
        .concat(this.webOrderCartsFGT)
        .concat(this.webOrderCartsPT)
        .includes(parseInt(this.order.CartID));
    },
    webOrderUrl() {
      if (this.webOrderCartsBB.includes(parseInt(this.order.CartID)))
        return process.env.VUE_APP_WEB_ORDER_BB + this.order.SourceOrderID;

      if (this.webOrderCartsFGT.includes(parseInt(this.order.CartID)))
        return process.env.VUE_APP_WEB_ORDER_FGT + this.order.SourceOrderID;

      if (this.webOrderCartsPT.includes(parseInt(this.order.CartID)))
        return process.env.VUE_APP_WEB_ORDER_PT + this.order.SourceOrderID;

      return null;
    },
    orderCanBeCancelled() {
      if (this.order.Cancelled == 1 || this.order.tracking?.data.length > 0)
        return false;
      return true;
    },
    warrantyCanBeAdded() {
      if (this.hasParentOrder) {
        return false;
      }
      if (
        this.diffInDays(new Date(), new Date(this.order.ExpectedShipDate)) >
        this.warrantyEnd
      ) {
        return false;
      }

      return !this.order.has_warranty;
    },
    guaranteeCanBeClaimed() {
      if (!this.order.details) {
        return false;
      }

      if (this.hasParentOrder) {
        return false;
      }

      if (!this.orderWasPlacedOnShopify && auth.isThirdPartyRestricted()) {
        return false;
      }

      let guaranteeable = this.order.details.filter((product) => {
        if (product.Adjustment != "0") {
          return false;
        }

        if (this.isOrderDetailCancelled(product)) {
          return false;
        }

        if (product.SKU.toUpperCase().includes("WARRANTY")) {
          return false;
        }

        if (!product.DateShipped) {
          return false;
        }
        if (
          this.diffInDays(new Date(), new Date(product.DateShipped)) >
          this.warrantyStart
        ) {
          return false;
        }

        return true;
      });

      return guaranteeable.length;
    },
    exceptionCanBeClaimed() {
      if (!this.order.details) {
        return false;
      }

      if (this.hasParentOrder) {
        return false;
      }

      let exceptionable = this.order.details.filter((product) => {
        if (product.Adjustment != "0") {
          return false;
        }

        if (this.isOrderDetailCancelled(product)) {
          return false;
        }

        return true;
      });

      return exceptionable.length;
    },
    dormancyCanBeClaimed() {
      if (!this.order.details) {
        return false;
      }

      if (this.hasParentOrder) {
        return false;
      }

      // available from March 1st through June 30th
      if ([2, 3, 4, 5].indexOf(new Date().getMonth()) == -1) {
        return false;
      }

      if (!this.orderWasPlacedOnShopify && auth.isThirdPartyRestricted()) {
        return false;
      }

      let dormants = this.order.details.filter((product) => {
        if (product.Adjustment != "0") {
          return false;
        }

        if (this.isOrderDetailCancelled(product)) {
          return false;
        }

        if (!product.DateShipped) {
          return false;
        }

        // only if shipped during previous winter months
        let dateShipped = new Date(product.DateShipped);
        if (
          this.diffInDays(new Date(), dateShipped) > 275 ||
          [4, 5, 6, 7, 8, 9].indexOf(dateShipped.getMonth()) > -1
        ) {
          return false;
        }

        return true;
      });

      return dormants.length;
    },
    warrantyCanBeClaimed() {
      if (!this.orderHasWarranty) {
        return false;
      }

      if (!this.order.details) {
        return false;
      }

      if (this.hasParentOrder) {
        return false;
      }

      if (!this.orderWasPlacedOnShopify && auth.isThirdPartyRestricted()) {
        return false;
      }

      if (!this.order.has_warranty) {
        return false;
      }

      let warrantable = this.order.details.filter((product) => {
        if (this.phase3d_care_app) {
          if (
            ["HARDGOOD", "INTERNAL"].includes(
              product?.variant?.product?.type.toUpperCase()
            )
          ) {
            return false;
          }
        } else {
          if (
            !["BAREROOT", "POTTED", "DROPSHIP", "PLANTS"].includes(
              product.inventory.Category?.toUpperCase()
            )
          ) {
            return false;
          }
        }

        if (product.Adjustment != "0") {
          return false;
        }

        if (this.isOrderDetailCancelled(product)) {
          return false;
        }

        if (!product.DateShipped) {
          return false;
        }
        let dateDiff = this.diffInDays(
          new Date(),
          new Date(product.DateShipped)
        );
        if (dateDiff > this.warrantyEnd || dateDiff <= this.warrantyStart) {
          return false;
        }

        return true;
      });

      return warrantable.length;
    },
    canEditEsd() {
      return !["In Fulfillment", "Complete"].includes(
        this.order.status?.Status
      );
    },
    restrictedOrder() {
      return ["Restricted"].includes(this.order.status?.Status);
    },
    userCanEmailCustomer() {
      if ([22, 23].indexOf(Number(this.order.CartID)) < 0) {
        return false;
      }

      if (auth.isUserAuthorized(["admin", "third-party"])) {
        return true;
      }

      return this.orderWasPlacedOnShopify;
    },
    userCanAddWarranty() {
      if (auth.isThirdPartyRestricted() && !this.orderWasPlacedOnShopify) {
        return false;
      }

      return true;
    },
    userCanProcessGuaranteeClaim() {
      if (auth.isThirdPartyRestricted() && !this.orderWasPlacedOnShopify) {
        return false;
      }

      return true;
    },
    userCanProcessDormancyClaim() {
      if (auth.isThirdPartyRestricted() && !this.orderWasPlacedOnShopify) {
        return false;
      }

      return true;
    },
    userCanProcessWarrantyClaim() {
      if (auth.isThirdPartyRestricted() && !this.orderWasPlacedOnShopify) {
        return false;
      }

      return true;
    },
    userCanProcessExceptionClaim() {
      return auth.isUserAuthorized(["admin", "supervisor", "third-party"]);
    },
    userCanUpdateFulfillmentStatus() {
      return auth.isUserAuthorized(["third-party"]);
    },
    canShowActions() {
      if (this.order.status?.Status === "Unprocessed") {
        return false;
      }
      return this.loaded;
    },
    orderHasWarranty() {
      return Boolean(this.order.has_warranty);
    },
    orderWasPlacedOnShopify() {
      return [22, 23, 31, 32].indexOf(Number(this.order.CartID)) > -1;
    },
    orderIsCancelled() {
      return Number(this.order.Cancelled);
    },
    orderIsFreight() {
      if (
        Number(this.order.freight_restricted) &&
        !auth.isUserAuthorized(["admin"])
      ) {
        return false;
      }
      return true;
    },
    orderCanBeReleased() {
      if (this.orderIsCancelled) {
        return false;
      }

      if (this.order.Designation === "On Hold") {
        if (
          this.order.status?.Status == "Hold" &&
          this.order.ExpectedFulfillmentDate == null
        ) {
          return true;
        } else {
          return auth.isUserAuthorized(["supervisor", "third-party"]);
        }
      }
      return false;
    },
    orderCanBeHold() {
      if (this.orderIsCancelled) {
        return false;
      }

      return this.order.Designation !== "On Hold";
    },
    hasParentOrder() {
      return (this.order?.relations || []).filter((relation) => {
        if (relation.type == "Parent") {
          return true;
        }

        return false;
      }).length;
    },
    orderStatus() {
      return this.order.status?.Status;
    },
  },
  methods: {
    toggleOrderActions() {
      this.showOrderActions = !this.showOrderActions;
    },
    openHoldModal() {
      this.toggleOrderActions();
      ModalBus.$emit("open", {
        component: ModalOrderHold,
        closeOnOutsideClick: false,
      });
    },
    openReleaseSlideover() {
      this.slideoverComponent = "OrderReleaseSlideover";
      this.slideoverTitle = "Release Order";
      this.toggleOrderActions();

      setTimeout(() => {
        this.slideoverVisible = true;
      }, 5);
    },
    openCancelModal() {
      this.toggleOrderActions();
      ModalBus.$emit("open", {
        component: ModalOrderCancel,
        closeOnOutsideClick: false,
      });
    },
    openWarrantyModal() {
      this.toggleOrderActions();
      ModalBus.$emit("open", {
        component: ModalAddWarranty,
        closeOnOutsideClick: false,
      });
    },
    openFulfillmentModal() {
      this.toggleOrderActions();
      ModalBus.$emit("open", {
        component: ModalOrderFulfillmentStatus,
        closeOnOutsideClick: false,
      });
    },
    openUpdateStateRestrictionModal() {
      this.toggleOrderActions();
      console.info("checking state restriction status");
      ModalBus.$emit("open", {
        component: OrderStateRestrictionUpdate,
        closeOnOutsideClick: false,
      });
    },
    openAdvancedSearchSlideover() {
      this.slideoverComponent = "OrderSearchSlideover";
      this.slideoverTitle = "Order Search";

      setTimeout(() => {
        this.slideoverVisible = true;
      }, 5);
    },
    openEmailCustomerSlideover() {
      this.slideoverComponent = "EmailCustomerSlideover";
      this.slideoverTitle = "Email Customer";
      this.toggleOrderActions();

      setTimeout(() => {
        this.slideoverVisible = true;
      }, 5);
    },
    openGuaranteeClaimSlideover() {
      this.slideoverComponent = "GuaranteeClaimSlideover";
      this.slideoverTitle = "Guarantee Claim";
      this.toggleOrderActions();

      setTimeout(() => {
        this.slideoverVisible = true;
      }, 5);
    },
    openDormancyClaimSlideover() {
      this.slideoverComponent = "DormancyClaimSlideover";
      this.slideoverTitle = "Dormancy Claim";
      this.toggleOrderActions();

      setTimeout(() => {
        this.slideoverVisible = true;
      }, 5);
    },
    openWarrantyClaimSlideover() {
      this.slideoverComponent = "WarrantyClaimSlideover";
      this.slideoverTitle = "Warranty Claim";
      this.toggleOrderActions();

      setTimeout(() => {
        this.slideoverVisible = true;
      }, 5);
    },
    openExceptionClaimSlideover() {
      this.slideoverComponent = "ExceptionClaimSlideover";
      this.slideoverTitle = "Exception Claim";
      this.toggleOrderActions();

      setTimeout(() => {
        this.slideoverVisible = true;
      }, 5);
    },
    openEditOrderExpectedShipDateSlideover() {
      this.slideoverComponent = "EditOrderExpectedShipDateSlideover";
      this.slideoverTitle = "Edit Order Expected Print Date";
      this.toggleOrderActions();

      setTimeout(() => {
        this.slideoverVisible = true;
      }, 5);
    },
    closeSlideover() {
      this.slideoverVisible = !this.slideoverVisible;
      setTimeout(() => {
        this.slideoverComponent = null;
      }, 505);
    },
    diffInDays(minuend, subtrahend) {
      return Math.ceil(
        (minuend.getTime() - subtrahend.getTime()) / (24 * 60 * 60 * 1000)
      );
    },
    isOrderDetailCancelled(detail) {
      return (
        detail?.Canceled === 1 || detail?.Product?.search(/^CANCELED/g) > -1
      );
    },
  },
  filters: {
    currency: (val) => {
      return moneyFormatter.format(val);
    },
    date: (val) => {
      if (val === null || val === undefined || val === "") return "--";
      return dateFormatter.format(val);
    },
  },
};
</script>
<style scoped>
.link {
  @apply ml-0 text-green-600 transition duration-150 ease-in-out;
}

.link:hover {
  @apply text-green-500 underline;
}

li {
  @apply inline-flex;
}
</style>
